import { useEffect, useState } from 'react';
import { adminApi, type FunnelResponse, type QualityResponse, type WorkersResponse } from './api/adminClient';
import KpiTile from './components/KpiTile';

type Range = '7d' | '30d' | 'all';

export default function AdminOverviewPage() {
  const [range, setRange] = useState<Range>('7d');
  const [funnel, setFunnel] = useState<FunnelResponse | null>(null);
  const [quality, setQuality] = useState<QualityResponse | null>(null);
  const [workers, setWorkers] = useState<WorkersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    Promise.all([
      adminApi.funnel(range),
      adminApi.quality(range === 'all' ? '30d' : range),
      adminApi.workers(),
    ])
      .then(([f, q, w]) => {
        setFunnel(f);
        setQuality(q);
        setWorkers(w);
      })
      .catch((e: Error) => setError(e.message));
  }, [range]);

  if (error) return <div className="alert alert-error">{error}</div>;
  if (!funnel || !quality || !workers) return <div className="p-8 text-center opacity-60">Cargando…</div>;

  const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Resumen</h1>
        <select className="select select-bordered select-sm" value={range} onChange={(e) => setRange(e.target.value as Range)}>
          <option value="7d">7 días</option>
          <option value="30d">30 días</option>
          <option value="all">Todo</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiTile title="Enviadas" value={funnel.totals.sent} />
        <KpiTile title="Tasa de respuesta" value={pct(funnel.rates.reply_rate)} tone="success" />
        <KpiTile title="Tasa de entrevistas" value={pct(funnel.rates.interview_rate)} tone="success" />
        <KpiTile title="En cola" value={funnel.totals.queued} />
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title">Funnel</h2>
          <div className="grid grid-cols-5 gap-2 text-center">
            {(['drafts', 'queued', 'sent', 'replied', 'interviews'] as const).map((k) => (
              <div key={k} className="p-3 bg-base-200 rounded">
                <div className="text-xs uppercase opacity-60">{k}</div>
                <div className="text-2xl font-bold">{funnel.totals[k]}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title">Tendencia (envíos vs respuestas)</h2>
          <Trend trend={funnel.trend} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiTile
          title="Bounce rate"
          value={pct(quality.bounce_rate)}
          tone={quality.bounce_rate > 0.05 ? 'error' : 'default'}
        />
        <KpiTile
          title="Gate fail rate"
          value={pct(quality.gate_failure_rate)}
          tone={quality.gate_failure_rate > 0.3 ? 'warning' : 'default'}
        />
        <KpiTile
          title="Token cost (USD)"
          value={`$${quality.token_cost.est_usd.toFixed(2)}`}
          subtitle={`${quality.token_cost.total_input.toLocaleString()} in / ${quality.token_cost.total_output.toLocaleString()} out`}
        />
      </div>

      <div className="card bg-base-100 shadow">
        <div className="card-body">
          <h2 className="card-title">Workers</h2>
          <div className="flex gap-3 flex-wrap">
            {workers.workers.map((w) => (
              <div key={w.name} className="flex items-center gap-2">
                <span
                  className={`badge ${
                    w.health === 'ok'
                      ? 'badge-success'
                      : w.health === 'stale'
                        ? 'badge-warning'
                        : w.health === 'error'
                          ? 'badge-error'
                          : 'badge-ghost'
                  }`}
                >
                  {w.name}
                </span>
                <span className="text-xs opacity-60">
                  {w.last_run_at ? new Date(w.last_run_at).toLocaleString() : 'never'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Trend({ trend }: { trend: FunnelResponse['trend'] }) {
  if (trend.length === 0) return <p className="opacity-60 py-8 text-center">Sin datos en el rango seleccionado.</p>;
  const max = Math.max(...trend.map((p) => p.sent), 1);
  const W = 600;
  const H = 160;
  const pad = 24;
  const stepX = (W - pad * 2) / Math.max(1, trend.length - 1);
  const path = (key: 'sent' | 'replied') =>
    trend
      .map((p, i) => {
        const x = pad + i * stepX;
        const y = H - pad - (p[key] / max) * (H - pad * 2);
        return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
      })
      .join(' ');
  return (
    <div className="flex items-center gap-4">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-3xl">
        <path d={path('sent')} fill="none" stroke="hsl(var(--p))" strokeWidth={2} />
        <path d={path('replied')} fill="none" stroke="hsl(var(--su))" strokeWidth={2} />
      </svg>
      <div className="flex flex-col gap-1 text-xs">
        <div className="flex items-center gap-1"><span className="w-3 h-1 bg-primary inline-block" /> Enviadas</div>
        <div className="flex items-center gap-1"><span className="w-3 h-1 bg-success inline-block" /> Respondidas</div>
      </div>
    </div>
  );
}
